import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import ObjectListEntry from "./ObjectListEntry";
import withUpdateStripes from "../misc/withUpdateStripes";
import { layoutObjectMatchesSearchString } from "../../reducers/exportlayouts";
import { getTreeSearch } from "../../reducers/search";

const ObjectList = ({ objects, selectedExportReport, updateStripes }) => {
    const treeSearch = useSelector(state => getTreeSearch(state));
    const [selectedObjectIds, setSelectedObjectIds] = useState([]);
    const [lastSelectedObjectId, setLastSelectedObjectId] = useState(null);

    useEffect(() => {
        updateStripes();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const objectIds = useMemo(() => Object.keys(objects), [objects]);

    const getVisibleObjectIds = () =>
        objectIds
            .filter(objectKey => {
                const object = objects[objectKey];
                return !(treeSearch && treeSearch != "" && !layoutObjectMatchesSearchString(object, treeSearch));
            })
            .map(objectKey => objects[objectKey].Id);

    const handleSelectObject = (event, objectId) => {
        const normalizedObjectId = Number(objectId);
        const hasMultiSelectModifier = event.ctrlKey || event.metaKey;

        if (event.shiftKey && (hasMultiSelectModifier || lastSelectedObjectId != null)) {
            const visibleObjectIds = getVisibleObjectIds();
            const startIndex = visibleObjectIds.indexOf(Number(lastSelectedObjectId));
            const endIndex = visibleObjectIds.indexOf(normalizedObjectId);

            if (startIndex === -1 || endIndex === -1) {
                setSelectedObjectIds([normalizedObjectId]);
                setLastSelectedObjectId(normalizedObjectId);
                return;
            }

            const [start, end] = startIndex <= endIndex ? [startIndex, endIndex] : [endIndex, startIndex];
            const rangeObjectIds = visibleObjectIds.slice(start, end + 1);

            if (hasMultiSelectModifier) {
                setSelectedObjectIds(previous => [...new Set([...previous, ...rangeObjectIds])]);
            } else {
                setSelectedObjectIds(rangeObjectIds);
            }

            setLastSelectedObjectId(normalizedObjectId);
            return;
        }

        if (hasMultiSelectModifier) {
            setSelectedObjectIds(previous =>
                previous.includes(normalizedObjectId)
                    ? previous.filter(id => id !== normalizedObjectId)
                    : [...previous, normalizedObjectId]
            );
            setLastSelectedObjectId(normalizedObjectId);
            return;
        }

        setSelectedObjectIds([normalizedObjectId]);
        setLastSelectedObjectId(normalizedObjectId);
    };

    const renderObject = objectId => {
        const object = objects[objectId];
        if (treeSearch && treeSearch != "" && !layoutObjectMatchesSearchString(object, treeSearch)) {
            return null;
        }
        return (
            <ObjectListEntry
                objectId={object.Id}
                object={object}
                key={object.Id}
                selectedExportReport={selectedExportReport}
                selectedObjectIds={selectedObjectIds}
                onSelectObject={handleSelectObject}
            />
        );
    };

    const layoutElements = objectIds.map(renderObject);

    return <div>{layoutElements}</div>;
};

ObjectList.propTypes = {
    //Inject by redux
    objects: PropTypes.any,
    treeSearch: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    selectedExportReport: PropTypes.any,
    // FROM HOC
    updateStripes: PropTypes.func.isRequired,
};

export default withUpdateStripes(ObjectList);
